/**
 * Alarms sub-menu, reached from the Live menu. Each alarm is a switch and a
 * value: turn it on, set the threshold, and it raises on the same warning banner
 * the rest of the safety alerts use. Two are wired now (charted-hazard proximity
 * and charted depth under the boat); the AIS pair is shown but locked, because
 * we have no AIS source feeding it yet and a switch that does nothing would just
 * mislead.
 */
import { AnchorPlacement, AnchorBoundaryShape } from './AnchorControls';
import { anchorPositionNow } from '@/ownship/anchorDrop';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAlarmsStore } from '@/store/alarms';
import { useSettingsStore } from '@/store/settings';
import { Sheet } from './Sheet';
import { haptic } from '@/ui/haptics';
import { GlossaryTerm } from '@/help/GlossaryTerm';
import { useChartPlotterStore } from '@/store';
import { useAnchorWatchStore, defaultScopeRatio } from '@/store/anchorWatch';
import { useSafetyStore } from '@/store/safety';
import { useEnvStore } from '@/store/environment';
import { swingRadiusNm, anchorReadout } from '@/safety';
import { chartedDepthAtNow } from '@/map/chartQuery';
import { heightAt, nextEventOfType } from '@/tides/heights';
import { tidePortNow } from '@/tides/tideNow';
import { useTideSourceStore } from '@/store/tideSources';
import { providerHasDepthData, NO_DEPTH_ON_SOURCE } from '@/chart-sources';

const pad2 = (n: number) => String(n).padStart(2, '0');
/** "UT", "UT−1", "UT+2" — the zone label carried against the anchor tide times. */
function zoneLabel(tzH: number): string {
  if (tzH === 0) return 'UT';
  return `UT${tzH > 0 ? '+' : '−'}${Math.abs(tzH)}`;
}
/** HH:MM of a real instant read in the port's zone (tzH hours east of UT). */
function fmtHM(ms: number, tzH: number): string {
  const d = new Date(ms + tzH * 3_600_000);
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

function Toggle({
  title,
  detail,
  on,
  onClick,
  locked,
}: {
  title: string;
  detail: string;
  on?: boolean;
  onClick?: () => void;
  locked?: boolean;
}) {
  return (
    <div className={`acp-layer${locked ? ' locked' : ''}`}>
      <div className="lt">
        <b>{title}</b>
        <span>{detail}</span>
      </div>
      <button
        type="button"
        className={`acp-sw${locked ? ' dis' : on ? ' on' : ''}`}
        aria-pressed={!!on}
        aria-label={title}
        disabled={locked}
        onClick={locked ? undefined : () => { haptic('select'); onClick?.(); }}
      />
    </div>
  );
}

/**
 * A labelled group of alarms with a header and a visual break, so the long sheet
 * reads as a handful of themed sections (depth, clearing, collision, and so on)
 * rather than one flat wall of switches. Purely presentational: the alarms inside
 * keep their own state and logic exactly as before.
 */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="acp-alarm-section">
      <h3 className="acp-alarm-section-h">{title}</h3>
      {children}
    </section>
  );
}

export function AlarmsSheet() {
  const config = useAlarmsStore((s) => s.config);
  const setConfig = useAlarmsStore((s) => s.setConfig);
  const masterMuted = useAlarmsStore((s) => s.masterMuted);
  const toggleMasterMuted = useAlarmsStore((s) => s.toggleMasterMuted);
  // Draught is the boat-profile value (single source of truth in the settings
  // store); editable here as well, but it's the same one figure.
  const draughtM = useSettingsStore((s) => s.draughtM);
  const setSettings = useSettingsStore((s) => s.set);

  // Anchor watch reads its own store; the live drag flag comes back through the
  // alarms pipeline like every other alarm.
  const anchor = useAnchorWatchStore((s) => s.anchor);
  const awScopeM = useAnchorWatchStore((s) => s.scopeM);
  const awDepthM = useAnchorWatchStore((s) => s.depthM);
  // Depth alarms below read the vector ENC; on a raster/tiles source there's
  // nothing to read, so we warn rather than let the skipper arm a dead alarm.
  const hasDepthData = useChartPlotterStore((s) => providerHasDepthData(s.chartProviderId));
  const awBowHeightM = useAnchorWatchStore((s) => s.bowHeightM);
  const dropAnchor = useAnchorWatchStore((s) => s.dropAnchor);
  const weighAnchor = useAnchorWatchStore((s) => s.weighAnchor);
  const setScopeM = useAnchorWatchStore((s) => s.setScopeM);
  const awScopeRatio = useAnchorWatchStore((s) => s.scopeRatio);
  const awRodeType = useAnchorWatchStore((s) => s.rodeType);
  const setScopeRatio = useAnchorWatchStore((s) => s.setScopeRatio);
  const setRodeType = useAnchorWatchStore((s) => s.setRodeType);
  const boatPos = useChartPlotterStore((s) => s.ownship.position);
  const liveAnchor = useAlarmsStore((s) => s.live.anchor);
  // The tide card's port + scrubber clock, so the anchor depth readout reads the
  // same tide the rest of the app shows, in the port's own time zone.
  const heightPortId = useEnvStore((s) => s.heightPortId);
  const chartProviderId = useChartPlotterStore((s) => s.chartProviderId);
  const heightSourceId = useTideSourceStore((s) => s.heightSourceId);
  const streamTimeMs = useEnvStore((s) => s.streamTimeMs);

  // Danger look-ahead cone: on/off, time vs distance, and the reach value. Lives
  // in the safety store (with the depth thresholds) so the cone geometry is in
  // one place; persisted there so it survives a restart.
  const coneOn = useSafetyStore((s) => s.settings.coneOn);
  const lookaheadMode = useSafetyStore((s) => s.settings.lookaheadMode);
  const lookaheadMin = useSafetyStore((s) => s.settings.lookaheadMin);
  const lookaheadNm = useSafetyStore((s) => s.settings.lookaheadNm);
  const setSafety = useSafetyStore((s) => s.setSettings);
  const [coneMin, setConeMin] = useState(String(lookaheadMin));
  const [coneNm, setConeNm] = useState(String(lookaheadNm));
  useEffect(() => setConeMin(String(lookaheadMin)), [lookaheadMin]);
  useEffect(() => setConeNm(String(lookaheadNm)), [lookaheadNm]);
  const commitConeMin = (v: string) => {
    setConeMin(v);
    const n = parseFloat(v);
    if (Number.isFinite(n) && n > 0) setSafety({ lookaheadMin: n });
  };
  const commitConeNm = (v: string) => {
    setConeNm(v);
    const n = parseFloat(v);
    if (Number.isFinite(n) && n > 0) setSafety({ lookaheadNm: n });
  };
  const [scope, setScope] = useState(String(awScopeM));
  useEffect(() => setScope(String(awScopeM)), [awScopeM]);
  // Placing the anchor off the bow by a bearing and distance, for when the hook
  // isn't right under us (we dropped back on the rode). Kept as text so typing is
  // smooth; parsed on the button press.
  const commitScope = (v: string) => {
    setScope(v);
    const n = parseFloat(v);
    if (Number.isFinite(n) && n > 0) setScopeM(n);
  };
  const [tideLimit, setTideLimit] = useState(String(config.tideHeightLimitM));
  useEffect(() => setTideLimit(String(config.tideHeightLimitM)), [config.tideHeightLimitM]);
  const commitTideLimit = (v: string) => {
    setTideLimit(v);
    const n = parseFloat(v);
    if (Number.isFinite(n) && n >= 0) setConfig({ tideHeightLimitM: n });
  };

  // Local text state so typing a threshold is smooth; the store updates as soon
  // as the value parses to a sensible positive number.
  const [proxNm, setProxNm] = useState(String(config.proximityNm));
  const [depthM, setDepthM] = useState(String(config.depthLimitM));
  const [draught, setDraught] = useState(String(draughtM));
  useEffect(() => setProxNm(String(config.proximityNm)), [config.proximityNm]);
  useEffect(() => setDepthM(String(config.depthLimitM)), [config.depthLimitM]);
  useEffect(() => setDraught(String(draughtM)), [draughtM]);

  const commitProx = (v: string) => {
    setProxNm(v);
    const n = parseFloat(v);
    if (Number.isFinite(n) && n > 0) setConfig({ proximityNm: n });
  };
  const commitDepth = (v: string) => {
    setDepthM(v);
    const n = parseFloat(v);
    if (Number.isFinite(n) && n > 0) setConfig({ depthLimitM: n });
  };
  const commitDraught = (v: string) => {
    setDraught(v);
    const n = parseFloat(v);
    if (Number.isFinite(n) && n >= 0) setSettings({ draughtM: n });
  };

  const [aisProx, setAisProx] = useState(String(config.aisProximityNm));
  const [aisCpa, setAisCpa] = useState(String(config.aisCpaNm));
  const [aisTcpa, setAisTcpa] = useState(String(config.aisTcpaMin));
  useEffect(() => setAisProx(String(config.aisProximityNm)), [config.aisProximityNm]);
  useEffect(() => setAisCpa(String(config.aisCpaNm)), [config.aisCpaNm]);
  useEffect(() => setAisTcpa(String(config.aisTcpaMin)), [config.aisTcpaMin]);
  const commitAisProx = (v: string) => {
    setAisProx(v);
    const n = parseFloat(v);
    if (Number.isFinite(n) && n > 0) setConfig({ aisProximityNm: n });
  };
  const commitAisCpa = (v: string) => {
    setAisCpa(v);
    const n = parseFloat(v);
    if (Number.isFinite(n) && n > 0) setConfig({ aisCpaNm: n });
  };
  const commitAisTcpa = (v: string) => {
    setAisTcpa(v);
    const n = parseFloat(v);
    if (Number.isFinite(n) && n > 0) setConfig({ aisTcpaMin: n });
  };

  const [gnssAcc, setGnssAcc] = useState(String(config.gnssAccuracyM));
  useEffect(() => setGnssAcc(String(config.gnssAccuracyM)), [config.gnssAccuracyM]);
  const commitGnssAcc = (v: string) => {
    setGnssAcc(v);
    const n = parseFloat(v);
    if (Number.isFinite(n) && n > 0) setConfig({ gnssAccuracyM: n });
  };

  const [batteryPct, setBatteryPct] = useState(String(config.lowBatteryPct));
  useEffect(() => setBatteryPct(String(config.lowBatteryPct)), [config.lowBatteryPct]);
  const commitBatteryPct = (v: string) => {
    setBatteryPct(v);
    const n = parseFloat(v);
    if (Number.isFinite(n) && n > 0 && n <= 100) setConfig({ lowBatteryPct: n });
  };

  // Anchor depth + tide readout at the drop: the charted depth there, plus the
  // depth at the next Low and High Water, and a rode sized from the High-Water
  // depth. Recomputed when the anchor, the boat profile, the scope, or the tide
  // clock moves. Null charted depth stays honest (raster source, or nothing
  // charted right there); null LW/HW means no tide data for this area.
  const anchorInfo = useMemo(() => {
    if (!anchor) return null;
    // No port means no tidal data for this water, and the readout below already
    // treats a null tide as chart datum, which is the cautious reading.
    const port = tidePortNow(chartProviderId, heightSourceId, heightPortId);
    const chartedM = hasDepthData ? chartedDepthAtNow(anchor) : null;
    const tideNowM = port ? heightAt(port, streamTimeMs) : null;
    const nextLw = port ? nextEventOfType(port, streamTimeMs, 'L') : null;
    const nextHw = port ? nextEventOfType(port, streamTimeMs, 'H') : null;
    const readout = anchorReadout({
      chartedM,
      tideNowM,
      tideLwM: nextLw?.heightM ?? null,
      tideHwM: nextHw?.heightM ?? null,
      draughtM,
      bowHeightM: awBowHeightM,
      scopeRatio: awScopeRatio,
    });
    return { port, readout, nextLw, nextHw, tideNowM };
  }, [anchor, hasDepthData, heightPortId, chartProviderId, heightSourceId, streamTimeMs, draughtM, awBowHeightM, awScopeRatio]);

  return (
    <Sheet
      name="alarms"
      title="Alarms"
      sub="Watches that run while we're under way and shout on the warning banner if something needs our eyes. Turn on the ones we want and set the level."
    >
      <div className={`acp-mastermute${masterMuted ? ' muted' : ''}`}>
        <div className="lt">
          <b>{masterMuted ? 'All alarms muted' : 'Mute all alarms'}</b>
          <span>
            {masterMuted
              ? 'Every alarm is still armed and watching, and the warning banner still shows. Only the sound, buzz and screen-off notification are silenced. Unmute to hear them again.'
              : 'Silences the sound, buzz and screen-off notification for every alarm at once, without turning any off. The alarms keep watching and still show on the banner.'}
          </span>
        </div>
        <button
          type="button"
          className={`acp-sw${masterMuted ? ' on' : ''}`}
          aria-pressed={masterMuted}
          aria-label="Mute all alarms"
          onClick={() => { haptic('commit'); toggleMasterMuted(); }}
        />
      </div>

      <Section title="Depth & grounding">
      <Toggle
        title="Shallow water under the keel (now)"
        detail="Warns when the clearance under the keel right now (charted depth + height of tide now − draught) drops below the level we set. The here-and-now reading, not a look-ahead."
        on={config.depthEnabled}
        onClick={() => setConfig({ depthEnabled: !config.depthEnabled })}
      />
      {config.depthEnabled && (
        <>
          <label className="acp-fieldlabel">Warn when clearance under keel below (m)</label>
          <input
            className="acp-text-input"
            value={depthM}
            onChange={(e) => commitDepth(e.target.value)}
            placeholder="1"
            inputMode="decimal"
            aria-label="Least depth under keel in metres"
          />
        </>
      )}
      {!hasDepthData && (
        <p className="acp-note" style={{ color: '#e0a03a' }}>
          {NO_DEPTH_ON_SOURCE}
        </p>
      )}

      <label className="acp-fieldlabel">Boat draught (m)</label>
      <input
        className="acp-text-input"
        value={draught}
        onChange={(e) => commitDraught(e.target.value)}
        placeholder="1.5"
        inputMode="decimal"
        aria-label="Boat draught in metres"
      />

      <Toggle
        title="Grounding ahead (look-ahead)"
        detail="Projects our track forward at the current course and speed and warns if it would reach water shallower than the safety depth, with the time to get there. Looks down our track, rather than at the water under us now."
        on={config.groundingEnabled}
        onClick={() => setConfig({ groundingEnabled: !config.groundingEnabled })}
      />
      {!hasDepthData && config.groundingEnabled && (
        <p className="acp-note" style={{ color: '#e0a03a' }}>
          {NO_DEPTH_ON_SOURCE}
        </p>
      )}
      <Toggle
        title="Danger cone"
        detail="Draws a cone ahead of the boat and flags wrecks, obstructions and shallow water inside it. This same reach sets how far the grounding look-ahead projects."
        on={coneOn}
        onClick={() => setSafety({ coneOn: !coneOn })}
      />
      {coneOn && (
        <>
          <div className="picks compact">
            <button
              type="button"
              className={lookaheadMode === 'distance' ? 'on' : ''}
              onClick={() => { haptic('tick'); setSafety({ lookaheadMode: 'distance' }); }}
            >
              <b>By distance</b>
            </button>
            <button
              type="button"
              className={lookaheadMode === 'time' ? 'on' : ''}
              onClick={() => { haptic('tick'); setSafety({ lookaheadMode: 'time' }); }}
            >
              <b>By time</b>
            </button>
          </div>
          {lookaheadMode === 'distance' ? (
            <>
              <label className="acp-fieldlabel">Look ahead (nm)</label>
              <input
                className="acp-text-input"
                value={coneNm}
                onChange={(e) => commitConeNm(e.target.value)}
                placeholder="0.5"
                inputMode="decimal"
                aria-label="Danger cone reach in nautical miles"
              />
            </>
          ) : (
            <>
              <label className="acp-fieldlabel">Look ahead (minutes at current speed)</label>
              <input
                className="acp-text-input"
                value={coneMin}
                onChange={(e) => commitConeMin(e.target.value)}
                placeholder="6"
                inputMode="decimal"
                aria-label="Danger cone reach in minutes"
              />
              <p className="acp-note">Reach is speed x minutes, so it grows with our speed. Near a stop it holds a short fixed reach so a danger right ahead still shows.</p>
            </>
          )}
        </>
      )}
      </Section>

      <Section title="Clearing guards">
      <Toggle
        title="Clearing-bearing guards"
        detail="Warn when we cross any clearing bearing to its unsafe side (each clearing has its own switch too)"
        on={config.clearingEnabled}
        onClick={() => setConfig({ clearingEnabled: !config.clearingEnabled })}
      />

      <Toggle
        title="Clearing-depth guards"
        detail="Warn when the charted depth under us crosses onto the shallow side of any do-not-cross depth contour (each one has its own switch too)"
        on={config.clearingDepthEnabled}
        onClick={() => setConfig({ clearingDepthEnabled: !config.clearingDepthEnabled })}
      />
      </Section>

      <Section title="Collision & traffic">
      <Toggle
        title="Charted hazard nearby"
        detail="Warns when a charted wreck, obstruction, shallow patch, or isolated danger comes inside the range we set"
        on={config.proximityEnabled}
        onClick={() => setConfig({ proximityEnabled: !config.proximityEnabled })}
      />
      {config.proximityEnabled && (
        <>
          <label className="acp-fieldlabel">Warn within (nm)</label>
          <input
            className="acp-text-input"
            value={proxNm}
            onChange={(e) => commitProx(e.target.value)}
            placeholder="0.25"
            inputMode="decimal"
            aria-label="Hazard proximity range in nautical miles"
          />
        </>
      )}

      <Toggle
        title="AIS target nearby"
        detail="Warns when an AIS vessel comes inside the range we set"
        on={config.aisProximityEnabled}
        onClick={() => setConfig({ aisProximityEnabled: !config.aisProximityEnabled })}
      />
      {config.aisProximityEnabled && (
        <>
          <label className="acp-fieldlabel">Warn within (nm)</label>
          <input
            className="acp-text-input"
            value={aisProx}
            onChange={(e) => commitAisProx(e.target.value)}
            placeholder="1"
            inputMode="decimal"
            aria-label="AIS proximity range in nautical miles"
          />
        </>
      )}

      <Toggle
        title="AIS target on a collision course (CPA)"
        detail="Warns when a target's closest point of approach is under the limit and it's closing within the time we set"
        on={config.aisCpaEnabled}
        onClick={() => setConfig({ aisCpaEnabled: !config.aisCpaEnabled })}
      />
      {config.aisCpaEnabled && (
        <>
          <label className="acp-fieldlabel"><GlossaryTerm termKey="cpa">CPA</GlossaryTerm> under (nm)</label>
          <input
            className="acp-text-input"
            value={aisCpa}
            onChange={(e) => commitAisCpa(e.target.value)}
            placeholder="0.5"
            inputMode="decimal"
            aria-label="CPA limit in nautical miles"
          />
          <label className="acp-fieldlabel">and closing within (min)</label>
          <input
            className="acp-text-input"
            value={aisTcpa}
            onChange={(e) => commitAisTcpa(e.target.value)}
            placeholder="20"
            inputMode="decimal"
            aria-label="TCPA limit in minutes"
          />
        </>
      )}
      <p className="acp-note">
        AIS alarms need AIS switched on with a source picked (Layers → AIS targets). Over the training
        area use the Simulated source; real vessels show on the Internet or NMEA sources.
      </p>
      </Section>

      <Section title="Position integrity">
      <Toggle
        title="Poor GNSS accuracy"
        detail="Warns when the fix's own horizontal accuracy is worse than the limit we set. Reads the accuracy the receiver reports, so it only means anything on a real GNSS, not the simulator."
        on={config.gnssAccuracyEnabled}
        onClick={() => setConfig({ gnssAccuracyEnabled: !config.gnssAccuracyEnabled })}
      />
      {config.gnssAccuracyEnabled && (
        <>
          <label className="acp-fieldlabel">Warn when accuracy worse than (m)</label>
          <input
            className="acp-text-input"
            value={gnssAcc}
            onChange={(e) => commitGnssAcc(e.target.value)}
            placeholder="25"
            inputMode="decimal"
            aria-label="GNSS accuracy limit in metres"
          />
        </>
      )}

      <Toggle
        title="Lost GNSS fix"
        detail="Warns when no fresh fix has arrived for a while (the same timeout that greys the boat), so a dropped real GNSS is flagged rather than sitting on the last position looking healthy."
        on={config.gnssLostEnabled}
        onClick={() => setConfig({ gnssLostEnabled: !config.gnssLostEnabled })}
      />

      <Toggle
        title="Sudden GNSS position jump (possible spoofing)"
        detail="Warns when the fix teleports further than any boat could really have moved between fixes, which can be a sign of GNSS spoofing or interference. Reuses the same jump detection that keeps a glitch off the track."
        on={config.gnssJumpEnabled}
        onClick={() => setConfig({ gnssJumpEnabled: !config.gnssJumpEnabled })}
      />
      <p className="acp-note">
        The GNSS alarms watch the real device GNSS only. On the Simulated source they stay quiet, because
        the training position is trusted.
      </p>
      </Section>

      <Section title="Anchor">
      <div className="acp-layer">
        <div className="lt">
          <b>Anchor watch</b>
          <span>
            {anchor
              ? `Anchored · swing ${Math.round(swingRadiusNm({ scopeM: awScopeM, depthM: awDepthM, bowHeightM: awBowHeightM }) * 1852)} m${liveAnchor ? ' · DRAGGING' : ''}`
              : 'Drop the anchor at the boat to start the swing-circle watch'}
          </span>
        </div>
        <button
          type="button"
          className={`acp-sw${anchor ? ' on' : ''}`}
          aria-pressed={!!anchor}
          aria-label="Anchor watch"
          onClick={() => {
            haptic('commit');
            if (anchor) weighAnchor();
            else if (boatPos) dropAnchor(anchorPositionNow(boatPos) ?? boatPos);
          }}
        />
      </div>
      <AnchorPlacement />
      {anchor && (
        <>
          <label className="acp-fieldlabel">Rode out / <GlossaryTerm termKey="scope">scope</GlossaryTerm> (m)</label>
          <input
            className="acp-text-input"
            value={scope}
            onChange={(e) => commitScope(e.target.value)}
            placeholder="30"
            inputMode="decimal"
            aria-label="Scope in metres"
          />

          <label className="acp-fieldlabel">Rode type / scope ratio</label>
          <div className="picks compact">
            <button
              type="button"
              className={awRodeType === 'chain' ? 'on' : ''}
              onClick={() => { haptic('tick'); setRodeType('chain'); setScopeRatio(defaultScopeRatio('chain')); }}
            >
              <b>Chain 4:1</b>
            </button>
            <button
              type="button"
              className={awRodeType === 'rope' ? 'on' : ''}
              onClick={() => { haptic('tick'); setRodeType('rope'); setScopeRatio(defaultScopeRatio('rope')); }}
            >
              <b>Rope 6:1</b>
            </button>
          </div>

          {anchorInfo && (
            <div className="acp-anchor-readout">
              <p className="acp-note">
                <b>Depth at the anchor</b>
                {anchorInfo.readout.chartedM != null
                  ? ` — ${anchorInfo.readout.chartedM.toFixed(1)} m charted (to chart datum).`
                  : hasDepthData
                    ? ' — nothing charted right here, so no depth to read.'
                    : ' — not available on this chart source.'}
              </p>
              {anchorInfo.readout.chartedM != null && (
                <>
                  {anchorInfo.readout.now.waterDepthM != null && (
                    <p className="acp-note">
                      Now: {anchorInfo.readout.now.waterDepthM.toFixed(1)} m of water
                      {anchorInfo.readout.now.underKeelM != null
                        ? ` (${anchorInfo.readout.now.underKeelM.toFixed(1)} m under the keel).`
                        : '.'}
                    </p>
                  )}
                  {anchorInfo.nextLw && anchorInfo.port ? (
                    <p className="acp-note">
                      Next LW {fmtHM(anchorInfo.nextLw.t, anchorInfo.port.tzH)} {zoneLabel(anchorInfo.port.tzH)}:{' '}
                      {anchorInfo.readout.lw.waterDepthM?.toFixed(1)} m of water
                      {anchorInfo.readout.lw.underKeelM != null
                        ? ` (${anchorInfo.readout.lw.underKeelM.toFixed(1)} m under the keel — the shallow extreme).`
                        : '.'}
                    </p>
                  ) : null}
                  {anchorInfo.nextHw && anchorInfo.port ? (
                    <p className="acp-note">
                      Next HW {fmtHM(anchorInfo.nextHw.t, anchorInfo.port.tzH)} {zoneLabel(anchorInfo.port.tzH)}:{' '}
                      {anchorInfo.readout.hw.waterDepthM?.toFixed(1)} m of water — the deep extreme, so what the
                      rode is sized on.
                    </p>
                  ) : null}
                  {!anchorInfo.nextLw && !anchorInfo.nextHw && (
                    <p className="acp-note">No tide data for this area, so LW/HW depth can&apos;t be worked out here.</p>
                  )}
                  {anchorInfo.port && (
                    <p className="acp-note">
                      Tide from {anchorInfo.port.name} ({zoneLabel(anchorInfo.port.tzH)}), the nearest station.
                    </p>
                  )}
                </>
              )}
              {anchorInfo.readout.rodeM != null && (
                <>
                  <p className="acp-note">
                    <b>Suggested rode</b> — {Math.round(anchorInfo.readout.rodeM)} m at {awScopeRatio}:1
                    {anchorInfo.nextHw ? ' (scope × depth at high water)' : ' (scope × depth now)'}.
                  </p>
                  <button
                    type="button"
                    className="acp-btn"
                    onClick={() => setScopeM(Math.round(anchorInfo.readout.rodeM as number))}
                  >
                    Use this rode ({Math.round(anchorInfo.readout.rodeM)} m)
                  </button>
                </>
              )}
            </div>
          )}

          <AnchorBoundaryShape />
        </>
      )}
      </Section>

      <Section title="Other">
      <Toggle
        title="Tide height limit"
        detail="Warns when the predicted height of tide now crosses a level we set, too low to float or clear a sill, or too high under a bridge"
        on={config.tideHeightEnabled}
        onClick={() => setConfig({ tideHeightEnabled: !config.tideHeightEnabled })}
      />
      {config.tideHeightEnabled && (
        <>
          <div className="picks compact">
            <button
              type="button"
              className={config.tideHeightMode === 'below' ? 'on' : ''}
              onClick={() => { haptic('tick'); setConfig({ tideHeightMode: 'below' }); }}
            >
              <b>Below</b>
            </button>
            <button
              type="button"
              className={config.tideHeightMode === 'above' ? 'on' : ''}
              onClick={() => { haptic('tick'); setConfig({ tideHeightMode: 'above' }); }}
            >
              <b>Above</b>
            </button>
          </div>
          <label className="acp-fieldlabel">Height limit (m above chart datum)</label>
          <input
            className="acp-text-input"
            value={tideLimit}
            onChange={(e) => commitTideLimit(e.target.value)}
            placeholder="1"
            inputMode="decimal"
            aria-label="Tide height limit in metres"
          />
        </>
      )}

      <Toggle
        title="Low battery"
        detail="Warns when the device battery drops below the level we set. It stays quiet while we're plugged in and charging, so it only sounds when the battery is genuinely on its way down."
        on={config.lowBatteryEnabled}
        onClick={() => setConfig({ lowBatteryEnabled: !config.lowBatteryEnabled })}
      />
      {config.lowBatteryEnabled && (
        <>
          <label className="acp-fieldlabel">Warn at or below (%)</label>
          <input
            className="acp-text-input"
            value={batteryPct}
            onChange={(e) => commitBatteryPct(e.target.value)}
            placeholder="15"
            inputMode="decimal"
            aria-label="Low battery threshold in percent"
          />
        </>
      )}
      </Section>

      <p className="acp-note">
        The hazard and depth alarms read the chart, not a live echo sounder or radar. Depth under the
        keel is the charted depth plus the predicted height of tide now, less the draught set here. A
        depth area only gives the depth for the whole area, so the shallow alarm can be cautious. Treat
        both as a prompt to check the chart and the instruments, not a measured clearance.
      </p>
    </Sheet>
  );
}
